/**
 * `{{variable}}` substitution, resolved to the bottom.
 *
 * Every substitution in the app used to be a single `String.replace` pass:
 *
 *     text.replace(/{{\s*([^}]+)\s*}}/g, (m, name) => lookup(name) ?? m)
 *
 * `replace` scans the SOURCE string once — whatever the callback returns is
 * never re-examined. So a variable whose value contains another reference, say
 * `base = "{{host}}/api"`, was pasted in verbatim and `{{host}}` survived into
 * the request. The URL that went out was not the URL anybody had configured.
 *
 * That one-pass shape existed in FIVE places — the request builder, the
 * collection runner, the SSE builder, the AI dry run and the code generator —
 * so fixing the one people happened to hit would have left four other ways to
 * reproduce it. This module is the single implementation they all use.
 *
 * ## Why cycles are a first-class concern
 *
 * Resolution that follows references has to survive `a = {{b}}`, `b = {{a}}`.
 * Users write those by accident, usually mid-rename, and an app that hangs or
 * blows the stack on a typo is worse than one that does not resolve at all.
 * A cycle here leaves the reference as literal text — visible, obviously
 * wrong, and harmless — rather than looping.
 */

const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;

/**
 * How many times a value may expand into another reference.
 *
 * Ten is far past anything real (`{{base}}` -> `{{host}}` -> `{{scheme}}` is
 * three) and low enough that a pathological chain costs nothing. Hitting it
 * means something is wrong with the data, so the remaining reference is left
 * as text for the same reason a cycle is.
 */
const MAX_DEPTH = 10;

/**
 * Builds a substitute function that resolves nested references.
 *
 * @param {(name: string) => (string|null|undefined)} lookup
 *   Resolves ONE name to its raw value, or null/undefined when unknown.
 *   Scope precedence belongs here — this module does not know about scopes.
 * @param {object} [options]
 * @param {(name: string) => (string|null)} [options.fallback]
 *   Consulted when `lookup` finds nothing. Used for dynamic variables
 *   ($randomUUID and friends), which are computed rather than stored.
 * @returns {(text: any) => any} Non-strings pass through untouched.
 */
export function createVariableResolver(lookup, { fallback } = {}) {
  return function resolve(text) {
    if (!text || typeof text !== 'string') return text;
    if (!text.includes('{{')) return text;

    // Names currently being expanded, so a value that refers back to one of
    // them is recognised as a cycle rather than followed forever.
    const expanding = new Set();

    const expand = (input, depth) => {
      if (depth > MAX_DEPTH || !input.includes('{{')) return input;

      return input.replace(VARIABLE_PATTERN, (match, rawName) => {
        const name = rawName.trim();

        // Already on the stack: this reference is part of a loop. Left as
        // literal text so the user sees exactly which name is circular.
        if (expanding.has(name)) return match;

        let value = lookup(name);
        if (value === null || value === undefined) {
          const dynamic = fallback?.(name);
          // Dynamic values are computed fresh and never contain references,
          // so they are returned without another pass.
          return dynamic !== null && dynamic !== undefined ? dynamic : match;
        }

        value = String(value);
        if (!value.includes('{{')) return value;

        // Recursing into the VALUE resolves it completely, which is why there
        // is no second pass over the finished string afterwards. An outer
        // re-scan would run outside this guard: `a = "x{{a}}y"` would then
        // expand ten times before the depth cap stopped it, returning
        // `xxxxxxxxxxx{{a}}yyyyyyyyyyy` instead of being recognised as the
        // self-reference it is.
        expanding.add(name);
        const expanded = expand(value, depth + 1);
        // Removed on the way out, so the same variable may legitimately appear
        // twice side by side — only a reference to a name still being expanded
        // is a cycle.
        expanding.delete(name);
        return expanded;
      });
    };

    return expand(text, 0);
  };
}

/**
 * Convenience for the common case: a flat list of `{key, value, enabled}` rows
 * in precedence order, HIGHEST first.
 */
export function createResolverFromLists(lists, options) {
  const lookup = (name) => {
    for (const list of lists) {
      if (!list) continue;
      const hit = list.find((v) => v?.enabled !== false && v?.key === name);
      if (hit) return hit.value;
    }
    return null;
  };
  return createVariableResolver(lookup, options);
}

/**
 * Every `{{name}}` referenced anywhere in a value, in first-seen order.
 *
 * Walks strings, arrays and plain objects, so a whole request can be handed in
 * rather than each field listed at the call site — which is what keeps a new
 * field from being silently missed the moment somebody adds one.
 *
 * Extraction only: it says which names are USED, never what they resolve to.
 * The regex for this was previously duplicated in eight places, all of them
 * building UI rather than sharing an answer.
 */
/**
 * Every `{{name}}` a value depends on — including the ones that only appear
 * inside another variable's VALUE.
 *
 * `extractVariableNames` answers "what does this text mention", which is not the
 * same question. A URL of `{{baseUrl}}/api/v1/` where `baseUrl` is
 * `example.com/{{hotelid}}` mentions one name and depends on two, and the second
 * is the one worth seeing: it is invisible until a request 404s, because
 * `hotelid` being undefined ships `{{hotelid}}` into the URL as literal text.
 *
 * Follows references the same way resolution does, and stops the same way: the
 * `seen` set doubles as the cycle guard (`a = {{b}}`, `b = {{a}}` terminates),
 * and MAX_DEPTH caps a pathological chain.
 *
 * @param {any} value          a string, or a whole request object
 * @param {(name: string) => (string|null|undefined)} lookup  name → raw value
 * @returns {{name: string, via: string|null, depth: number}[]}
 *   In first-seen order. `via` is the variable whose value introduced this one,
 *   null for names the request itself mentions.
 */
export function extractVariableDependencies(value, lookup) {
  const direct = extractVariableNames(value);
  const seen = new Set(direct);
  const out = direct.map((name) => ({ name, via: null, depth: 0 }));

  // Breadth-first, so a name is attributed to the SHALLOWEST variable that
  // introduced it — which is the one the user can act on.
  //
  // A COPY, not `out` itself: `for…of` over an array sees elements appended
  // while it runs, so aliasing them made one pass swallow every level at once —
  // every name came back at depth 1 and MAX_DEPTH never got a chance to cap a
  // 100-link chain.
  let frontier = [...out];
  for (let depth = 1; depth <= MAX_DEPTH && frontier.length; depth += 1) {
    const next = [];
    for (const parent of frontier) {
      const raw = lookup?.(parent.name);
      if (raw === null || raw === undefined) continue;
      for (const name of extractVariableNames(String(raw))) {
        if (seen.has(name)) continue;        // also how a cycle ends
        seen.add(name);
        const row = { name, via: parent.name, depth };
        out.push(row);
        next.push(row);
      }
    }
    frontier = next;
  }

  return out;
}

export function extractVariableNames(value, seen = new Set(), out = []) {
  if (typeof value === 'string') {
    if (value.includes('{{')) {
      // A fresh regex each call: the shared one is /g and therefore carries
      // lastIndex between calls, which silently skips matches.
      const re = new RegExp(VARIABLE_PATTERN.source, 'g');
      let m;
      while ((m = re.exec(value)) !== null) {
        const name = m[1].trim();
        if (name && !seen.has(name)) {
          seen.add(name);
          out.push(name);
        }
      }
    }
    return out;
  }

  if (Array.isArray(value)) {
    for (const item of value) extractVariableNames(item, seen, out);
    return out;
  }

  if (value && typeof value === 'object') {
    for (const key of Object.keys(value)) extractVariableNames(value[key], seen, out);
    return out;
  }

  return out;
}
